import { Injectable } from '@angular/core';
import {
  AlertController,
  LoadingController,
  ToastController,
  ModalController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { Environment } from 'user.interface';
import { APP_CONSTANTS } from 'app/app.constants';
import { UserStore } from 'app/stores/user.store';
import { AnalyticsService } from './analytics.service';
import { VideoPopupComponent } from 'app/components/video-popup/video-popup.component';
import { Capacitor } from '@capacitor/core';

/**
 * A generic service for custom utility methods. Modify this file as needed.
 */
@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  private loader: HTMLIonLoadingElement;
  private loaderActive = false;
  public isDesktop = false;
  public device = APP_CONSTANTS.DEVICE.MOBILE;
  public selectedPage = '';

  constructor(
    private readonly loadingCtrl: LoadingController,
    private readonly translate: TranslateService,
    protected alertCtrl: AlertController,
    public toastController: ToastController,
    protected analyticsService: AnalyticsService,
    protected userStore: UserStore,
    public modalController: ModalController
  ) {}

  public async showLoading(message?: string) {
    if (this.loaderActive === false) {
      this.loaderActive = true;
      this.loader = await this.loadingCtrl.create({
        message: message || this.translate.instant('PLEASE_WAIT'),
        spinner: 'circles',
        mode: 'ios',
        translucent: true,
        backdropDismiss: true,
        cssClass: 'loading',
      });

      return this.loader.present();
    }
  }

  public async showToast(text: string) {
    const toast = await this.toastController.create({
      message: text,
      duration: 2000,
    });
    toast.present();
  }

  public hideLoading() {
    if (this.loader) {
      setTimeout(() => {
        this.loader.dismiss();
      }, 500);
    }
    this.loaderActive = false;
  }

  public async playVideo(videoUrl) {
    const videoModal = await this.modalController.create({
      component: VideoPopupComponent,
      cssClass: 'video-popup',
      componentProps: {
        videoSrc: videoUrl,
      },
      backdropDismiss: true,
    });
    await videoModal.present();
  }

  public async showApiFailure(failureCode = 'N/A') {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('API_ERROR.TITLE'),
      message: this.translate.instant('API_ERROR.DESCRIPTION', { failureCode }),
      buttons: [this.translate.instant('OK')],
    });

    return alert.present();
  }

  public async showNonProdFormToast() {
    if (this.getDemoAccountValue('email') === '') {
      return;
    }
    const toast = await this.toastController.create({
      message:
        'Looks like this is a testing environment so this form has been pre-filled to save you a minute. Enjoy! ☕️',
      duration: 5000,
      position: 'bottom',
      color: 'dark',
      keyboardClose: true,
      buttons: [{ text: 'OK', role: 'cancel' }],
    });
    setTimeout(() => {
      toast.present();
    }, 250);
  }

  public getDemoAccountValue(key: string) {
    if (!(environment as unknown as Environment).demoAccountCredentials) {
      return '';
    } else {
      return (environment as unknown as Environment).demoAccountCredentials[
        key
      ];
    }
  }

  public async showInvalidURLToast() {
    if (this.getDemoAccountValue('email') === '') {
      return;
    }
    const toast = await this.toastController.create({
      message: this.translate.instant('RESOURCES.INVALID_URL'),
      duration: 3000,
      position: 'bottom',
      color: 'dark',
      keyboardClose: true,
      buttons: [{ text: 'OK', role: 'cancel' }],
    });
    setTimeout(() => {
      toast.present();
    }, 250);
  }

  public async showAlertWithOKButton(title: string, msg: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      message: msg,
      buttons: [this.translate.instant('OK')],
      cssClass: 'alert',
      backdropDismiss: false,
    });
    alert.present();

    return alert;
  }

  public async showAlert(title: string, msg: string, buttonList) {
    const alert = await this.alertCtrl.create({
      header: title ?? null,
      message: msg,
      buttons: buttonList,
      backdropDismiss: false,
      cssClass: 'alert',
    });

    return alert.present();
  }

  public hideAlert() {
    this.alertCtrl.dismiss();
  }

  public getDeviceType() {
    const deviceType =
      document.documentElement.clientWidth >= APP_CONSTANTS.TABLET_MIN_WIDTH
        ? APP_CONSTANTS.DEVICE.TABLET
        : APP_CONSTANTS.DEVICE.MOBILE;

    return document.documentElement.clientWidth <=
      APP_CONSTANTS.TABLET_MAX_WIDTH
      ? deviceType
      : APP_CONSTANTS.DEVICE.DESKTOP;
  }

  public isDarkTheme() {
    return !!localStorage.getItem('dark');
  }

  public getImageName(path: string, fileExtention: string) {
    let imageName;
    if (this.getDeviceType() === APP_CONSTANTS.DEVICE.MOBILE) {
      imageName = `${path}-${APP_CONSTANTS.DEVICE.MOBILE}`;
    } else if (this.getDeviceType() === APP_CONSTANTS.DEVICE.TABLET) {
      imageName = `${path}-${APP_CONSTANTS.DEVICE.TABLET}`;
    } else {
      imageName = `${path}-${APP_CONSTANTS.DEVICE.DESKTOP}`;
    }

    if (this.isDarkTheme()) {
      imageName = `${imageName}-${APP_CONSTANTS.THEME.DARK}${fileExtention}`;
    } else {
      imageName = `${imageName}-${APP_CONSTANTS.THEME.LIGHT}${fileExtention}`;
    }

    return imageName;
  }

  public showHCPPopup() {
    const title = this.translate.instant('ARE_YOU_HCP_TITLE');
    const msg = this.translate.instant('ARE_YOU_HCP_MSG');
    const buttons = [
      {
        text: this.translate.instant('CANCEL'),
        handler: () => {
          this.analyticsService.trackAction(
            this.selectedPage,
            APP_CONSTANTS.HCP.CANCEL
          );
          if (Capacitor.getPlatform() === 'web') {
            window.location.href = APP_CONSTANTS.PFIZER_HOME_PAGE;
            setTimeout(() => {
              this.showHCPPopup();
            }, 750);
          } else {
            this.userStore.isHCP = false;
            this.hideAlert();
            this.showHCPErrorPopup();
          }
        },
      },
      {
        text: this.translate.instant('CONTINUE'),
        handler: () => {
          this.userStore.isHCP = true;
          this.analyticsService.trackAction(
            this.selectedPage,
            APP_CONSTANTS.HCP.CONTINUE
          );
          this.hideAlert();
        },
      },
    ];
    this.showAlert(title, msg, buttons);
  }

  public showHCPErrorPopup() {
    const title = this.translate.instant('TERMS.DISAGREE_MESSAGE_TITLE');
    const msg = this.translate.instant('HCP_ERROR_MSG');
    const buttons = [
      {
        text: this.translate.instant('OK'),
        handler: () => {
          this.hideAlert();
        },
      },
    ];
    this.showAlert(title, msg, buttons);
  }
}
